import { useReadContract } from 'wagmi';
import { type Address } from 'viem';
import { TOKEN_ABI, getNetworkConfig } from '../constants';

interface UseContractReadsParams {
  address: Address | undefined;
  sourceChainId: number;
}

/**
 * Hook for reading bridge contract data
 * Fetches token balance, decimals, and allowance for the source chain
 */
export const useContractReads = ({ address, sourceChainId }: UseContractReadsParams) => {
  const networkConfig = getNetworkConfig(sourceChainId);
  const tokenAddress = networkConfig?.tokenAddress;
  const adapterAddress = networkConfig?.adapterAddress;

  // Read token decimals
  const { data: decimals } = useReadContract({
    address: tokenAddress,
    abi: TOKEN_ABI,
    functionName: 'decimals',
    chainId: sourceChainId,
  });

  // Read user's token balance
  const {
    data: balance,
    refetch: refetchBalance,
    isLoading: isBalanceLoading,
  } = useReadContract({
    address: tokenAddress,
    abi: TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: sourceChainId,
    query: {
      enabled: !!address && !!tokenAddress,
    },
  });

  // Read current allowance for the adapter
  const {
    data: allowance,
    refetch: refetchAllowance,
    isLoading: isAllowanceLoading,
  } = useReadContract({
    address: tokenAddress,
    abi: TOKEN_ABI,
    functionName: 'allowance',
    args: address && adapterAddress ? [address, adapterAddress] : undefined,
    chainId: sourceChainId,
    query: {
      enabled: !!address && !!tokenAddress && !!adapterAddress,
    },
  });

  return {
    tokenAddress,
    adapterAddress,
    decimals: decimals ?? 18,
    balance,
    allowance,
    refetchBalance,
    refetchAllowance,
    isBalanceLoading,
    isAllowanceLoading,
  };
};
